import { useState, useEffect, useContext, useRef } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

import { ReactComponent as PlusIcon } from "assets/images/icons/Plus.svg";
import testIds from "constants/testIds";
import { IOtherPermissionProps, IPermissionProps } from "components/molecules/Navigation/interface/sidebar";
import usePermissions from "hooks/usePermissions";
import { ReactComponent as ShipIcon } from "assets/images/icons/Ship.svg";
import { ReactComponent as CalculatorIcon } from "assets/images/icons/Calculator.svg";
import { ReactComponent as BoltIcon } from "assets/images/icons/Bolt.svg";
import { ReactComponent as TrackerIcon } from "assets/images/icons/TrackerIcon.svg";

import NavContext from "./NavContext";
import RequestSelectionModal from "../shipper/RequestSelectionModal";

const NewRequest = function ({
  permissions,
  otherPermissions,
}: {
  permissions: IPermissionProps;
  otherPermissions: IOtherPermissionProps;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { navigationConfig, setNavigationConfig } = useContext(NavContext);
  const { isRequestPopupOpen } = navigationConfig;
  const [onHover, setOnHover] = useState(false);
  const [showRequestSelectionModal, setShowRequestSelectionModal] = useState(false);
  const [requestSelectionModalConfig, setRequestSelectionModalConfig] = useState<{
    requestType: "quick_request" | "quote_request";
    showOCRSelection: boolean;
    showConsolidationSelection: boolean;
  }>({
    requestType: "quick_request",
    showOCRSelection: false,
    showConsolidationSelection: false,
  });

  const { create_quick_requests, create_quote_requests } = permissions;
  const { tender_based_shipment: { create_shipment } = {}, off_platform: { create_tracker } = {} } = otherPermissions;

  const { permissions: userPermissions } = usePermissions();
  const { email } = JSON.parse(userPermissions || "{}");
  const { company_shipper_settings = {}, permissions: userRolePermissions = {} } = JSON.parse(userPermissions || "{}") || {};
  const showMBL = import.meta.env.REACT_APP_ENVIRONMENT_NAME === "qa" || email === "gearbox@cargobase.com";
  const canConsolidateOrders = otherPermissions?.quoting?.can_consolidate_orders;
  const isOCRAllowedForCompany = company_shipper_settings?.enable_ocr_packing_list;
  const isOCRAllowedForUser = userRolePermissions?.quoting?.can_create_request_from_file_upload;
  const canPerformOCROnPackingList = isOCRAllowedForCompany && isOCRAllowedForUser;
  const trackerPermission = create_tracker && company_shipper_settings?.allow_off_platform_shipments;

  useEffect(() => {
    const checkIfClickedOutside = (e: any) => {
      if (isRequestPopupOpen && ref?.current && !ref?.current.contains(e.target)) {
        setNavigationConfig((prev: any) => ({ ...prev, isRequestPopupOpen: false }));
      }
    };
    document.addEventListener("mousedown", checkIfClickedOutside);
    return () => {
      document.removeEventListener("mousedown", checkIfClickedOutside);
    };
  }, [isRequestPopupOpen]);

  if (!create_quick_requests && !create_quote_requests && !create_shipment && !trackerPermission) return null;

  const toggleNavigation = (route: string) => {
    setNavigationConfig({
      ...navigationConfig,
      isShown: navigationConfig.expanded && navigationConfig.isMobile ? false : navigationConfig.isShown,
    });
    navigate(route);
  };

  const allOptions = [create_quick_requests, create_quote_requests, create_shipment, trackerPermission].filter((x) => x);

  const handleClickQuickRequest = () => {
    if (create_quick_requests && canPerformOCROnPackingList) {
      setShowRequestSelectionModal(true);
      setRequestSelectionModalConfig({
        requestType: "quick_request",
        showOCRSelection: true,
        showConsolidationSelection: false,
      });
    } else toggleNavigation("/quick-request");
    setNavigationConfig((prev: any) => ({ ...prev, isRequestPopupOpen: false }));
  };

  const handleClickQuoteRequest = () => {
    if (create_quote_requests && (canConsolidateOrders || canPerformOCROnPackingList)) {
      setShowRequestSelectionModal(true);
      setRequestSelectionModalConfig({
        requestType: "quote_request",
        showOCRSelection: canPerformOCROnPackingList,
        showConsolidationSelection: !!canConsolidateOrders,
      });
    } else toggleNavigation("/quote-request");
    setNavigationConfig((prev: any) => ({ ...prev, isRequestPopupOpen: false }));
  };

  const handleToggle = () => {
    if (allOptions.length === 1) {
      if (create_quick_requests) handleClickQuickRequest();
      else if (create_quote_requests) handleClickQuoteRequest();
      else if (create_shipment) navigate("/shipment");
      else if (trackerPermission) navigate("/tracker");
    } else {
      setNavigationConfig((prev: any) => ({ ...prev, isRequestPopupOpen: !prev.isRequestPopupOpen }));
    }
  };

  return (
    <div className="position-relative mt-32px">
      <RequestSelectionModal
        show={showRequestSelectionModal}
        onHide={() => setShowRequestSelectionModal(false)}
        requestSelectionModalConfig={requestSelectionModalConfig}
      />
      <div role="presentation" ref={ref} data-testid={testIds.MENU_ADD_BUTTON}>
        <button
          className={`navigation-item new-request-button text-white cursor-pointer mt-4 ${
            onHover ? "bg-blue-dark" : "bg-blue"
          } ${isRequestPopupOpen ? "rotate-icon" : ""}`}
          onMouseEnter={() => setOnHover(true)}
          onMouseLeave={() => setOnHover(false)}
          id="new-button"
          onClick={handleToggle}
          tabIndex={0}
          type="button"
        >
          <div
            className={`d-flex align-items-center w-100 ${navigationConfig.expanded ? "" : "justify-content-center"}`}
            style={{ gap: "10px" }}
          >
            <div
              className="align-items-center justify-content-center d-flex"
              style={{
                transition: "all .2s ease",
                width: navigationConfig.expanded ? "24px" : "auto",
              }}
            >
              <PlusIcon />
            </div>
            {navigationConfig.expanded && (
              <div className="navigation-item-label">
                <FormattedMessage id="components.molecules.navigation.newRequest" defaultMessage="New Request" />
              </div>
            )}
          </div>
        </button>

        {isRequestPopupOpen && (
          <div
            className={`d-flex flex-column justify-content-center border-grey sidebar-tooltip ${
              navigationConfig.expanded ? "tooltip-position" : "sidebar-tooltip"
            }`}
            style={{ width: "191px", height: "auto", padding: "16px" }}
          >
            {create_quick_requests && (
              <div
                className="d-flex align-items-center cursor-pointer font-b3 bg-hover-palegrey br-16"
                id="new-quick-request"
                role="presentation"
                data-testid={testIds.MENU_ADD_QUICK_REQUEST_BUTTON}
                onClick={handleClickQuickRequest}
                style={{ height: "40px", paddingLeft: "7px" }}
              >
                <BoltIcon className="mr-2" />
                <span>
                  <FormattedMessage id="components.molecules.navigation.quickRequest" defaultMessage="Quick Request" />
                </span>
              </div>
            )}
            {create_quote_requests && (
              <div
                className="d-flex align-items-center cursor-pointer font-b3 bg-hover-palegrey br-16"
                id="new-request"
                role="presentation"
                data-testid={testIds.MENU_ADD_QUOTE_REQUEST_BUTTON}
                onClick={handleClickQuoteRequest}
                style={{ height: "40px", paddingLeft: "7px" }}
              >
                <CalculatorIcon className="mr-2" />
                <span>
                  <FormattedMessage id="components.molecules.navigation.quoteRequest" defaultMessage="Quote Request" />
                </span>
              </div>
            )}
            {create_shipment && (
              <div
                className="d-flex align-items-center cursor-pointer font-b3 bg-hover-palegrey br-16"
                id="new-shipment"
                role="presentation"
                data-testid={testIds.MENU_ADD_SHIPMENT_BUTTON}
                onClick={() => {
                  toggleNavigation("/create-new-booking");
                  setNavigationConfig((prev: any) => ({ ...prev, isRequestPopupOpen: !prev.isRequestPopupOpen }));
                }}
                style={{ height: "40px", paddingLeft: "7px" }}
              >
                <ShipIcon className="mr-2" />
                <span>
                  <FormattedMessage id="new-request.form.newShipment" defaultMessage="New Shipment" />
                </span>
              </div>
            )}
            {/* <div className="d-flex align-items-center cursor-pointer font-b3 bg-hover-palegrey br-16"> */}
            {/* Uncomment this when we need to show mini tender in side menu. */}
            {/* <div
                    className="d-flex align-items-center cursor-pointer font-12 bg-hover-palegrey br-16"
                    id="new-mini-tender"
                    role="presentation"
                    onClick={() => {
                      toggleNavigation("/mini-tender");
                    }}
                    style={{ height: "40px", margin: "4px 0px", paddingLeft: "7px" }}
                  >
                    <MiniTenderIcon className="mr-2" />
                    <span>
                      <FormattedMessage id="components.molecules.navigation.miniTender" defaultMessage="Mini Tender" />
                    </span>
                  </div> */}
            {/* </div> */}

            {trackerPermission && (
              <div
                className="d-flex align-items-center cursor-pointer font-b3 bg-hover-palegrey br-16"
                id="tracker"
                role="presentation"
                data-testid={testIds.MENU_ADD_TRACKER_BUTTON}
                onClick={() => {
                  toggleNavigation("/tracker");
                  setNavigationConfig((prev: any) => ({ ...prev, isRequestPopupOpen: !prev.isRequestPopupOpen }));
                }}
                style={{ height: "40px", paddingLeft: "7px" }}
              >
                <TrackerIcon className="mr-2" />
                <span>
                  <FormattedMessage id="general.tracker" defaultMessage="Tracker" />
                </span>
              </div>
            )}

            {showMBL && (
              <div
                className="d-flex align-items-center cursor-pointer font-b3 bg-hover-palegrey br-16"
                id="tracker"
                role="presentation"
                data-testid={testIds.MENU_ADD_MBL_BUTTON}
                onClick={() => {
                  toggleNavigation("/MBL-shipment");
                  setNavigationConfig((prev: any) => ({ ...prev, isRequestPopupOpen: false }));
                }}
                style={{ height: "40px", paddingLeft: "7px" }}
              >
                <TrackerIcon className="mr-2" />
                <span>
                  <FormattedMessage id="general.MBLShipment" defaultMessage="MBL Shipment" />
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* </OverlayTrigger> */}
      {!navigationConfig.expanded && (
        <div
          style={{
            transition: "all .2s ease",
            opacity: onHover ? "100%" : "0%",
            transform: onHover ? "translateX(15%)" : "translateX(-20%)",
          }}
          className="navigation-side-label navigation-new-request bg-blue-dark"
        >
          <FormattedMessage id="components.molecules.navigation.newRequest" defaultMessage="New Request" />
        </div>
      )}
    </div>
  );
};

NewRequest.propTypes = {
  permissions: PropTypes.instanceOf(Object).isRequired,
  otherPermissions: PropTypes.instanceOf(Object).isRequired,
};

export default NewRequest;
